import { Box, Chip, Typography } from '@mui/material';
import { ReactComponent as CalendarIcon } from 'assets/calendar.svg';
import { ReactComponent as WarningOrangeIcon } from 'assets/error-orange.svg';
import { ReactComponent as WarningRedIcon } from 'assets/error-red.svg';
import { ReactComponent as UserIcon } from 'assets/user-black.svg';
import { ReactComponent as VerticalDotsIcon } from 'assets/vertical-dots.svg';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import dayjs from 'dayjs';
import { formatDealAmount } from 'helper/short-currency';
import { MouseEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import RoutePaths from 'routes/RoutePaths';
import zeplinColor from 'theme/Color';
import styles from './DealCard.module.css';

type CardDealType = undefined | 'warning' | 'overdue';

const DealCardColors = {
  warning: zeplinColor.AlertInfo,
  overdue: zeplinColor.Error,
};

const Date = ({
  type,
  date,
  daysDiff,
  velocityTargetDate,
}: {
  type: CardDealType;
  date: string;
  daysDiff?: number | string;
  velocityTargetDate?: number | string;
}) => {
  if (!date) return <Typography>N/A</Typography>;
  const formattedDate = dayjs(date).format('D MMM, YYYY');

  if (type === 'overdue') {
    const value =
      velocityTargetDate !== undefined && velocityTargetDate !== null
        ? Math.abs(
            typeof velocityTargetDate === 'string'
              ? parseFloat(velocityTargetDate)
              : velocityTargetDate,
          )
        : daysDiff !== undefined && daysDiff !== null
          ? Math.abs(typeof daysDiff === 'string' ? parseFloat(daysDiff) : daysDiff)
          : undefined;

    const toolTipText =
      value !== undefined
        ? `Deal exceeded velocity target by ${value} days`
        : 'Deal exceeded velocity target';

    return (
      <LightTooltip
        title={
          <Typography variant='body3' color={zeplinColor.Neutral1}>
            {toolTipText}
          </Typography>
        }
        placement='bottom-start'
        style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <CalendarIcon />
          <Typography variant='responsiveBody3' sx={{ color: DealCardColors.overdue }}>
            {formattedDate}
          </Typography>
          <WarningRedIcon />
        </Box>
      </LightTooltip>
    );
  }

  if (type === 'warning') {
    const value =
      velocityTargetDate !== undefined && velocityTargetDate !== null
        ? typeof velocityTargetDate === 'string'
          ? parseFloat(velocityTargetDate)
          : velocityTargetDate
        : daysDiff !== undefined && daysDiff !== null
          ? typeof daysDiff === 'string'
            ? parseFloat(daysDiff)
            : daysDiff
          : undefined;

    const toolTipText =
      value !== undefined
        ? `Deal approaching velocity target in ${value} days`
        : 'Deal approaching velocity target';

    return (
      <LightTooltip
        title={
          <Typography variant='body3' color={zeplinColor.Neutral1}>
            {toolTipText}
          </Typography>
        }
        placement='bottom-start'
        style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <CalendarIcon />
          <Typography variant='responsiveBody3' sx={{ color: DealCardColors.warning }}>
            {formattedDate}
          </Typography>
          <WarningOrangeIcon />
        </Box>
      </LightTooltip>
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: '4px' }}>
      <CalendarIcon />
      <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
        {formattedDate}
      </Typography>
    </Box>
  );
};

const DealCard = ({
  data,
  onDrag,
  onSettingsClick,
  variant = 'base',
}: {
  data: any;
  onDrag: (e: React.DragEvent, dealTitle: string) => void;
  onSettingsClick: (event: MouseEvent<SVGSVGElement>, deal: any) => void;
  variant?: 'base' | 'no-pipeline';
}) => {
  const {
    name,
    clientName,
    dealValue,
    pipelineType,
    stageName,
    ownerName,
    closeDate,
    profilePicture,
    needsAttention,
    dealHealth,
    dealHealthName,
    velocityTargetDate,
    daysDiff,
    amount,
  } = data;

  const formatPipelineType = (value: string) => {
    if (!value) return value;
    return value
      .replace(/_/g, '/')
      .split('/')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('/');
  };

  const formatStageName = (value: string) => {
    if (!value) return value;
    return value
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formattedPipelineType = pipelineType ? formatPipelineType(pipelineType) : pipelineType;
  const formattedStageName = stageName ? formatStageName(stageName) : stageName;

  const getCardColor = (): { borderColor: string; dotColor: string } => {
    const attentionStatus = needsAttention ? String(needsAttention).toLowerCase() : null;
    const health = dealHealth || dealHealthName;
    const healthLower = health ? String(health).toLowerCase() : '';

    if (attentionStatus === 'overdue' || healthLower === 'overdue') {
      return {
        borderColor: zeplinColor.Error,
        dotColor: zeplinColor.Error,
      };
    }

    if (attentionStatus === 'warning' || healthLower === 'warning') {
      return {
        borderColor: zeplinColor.AlertInfo,
        dotColor: zeplinColor.AlertInfo,
      };
    }

    return {
      borderColor: zeplinColor.Neutral5,
      dotColor: zeplinColor.Success,
    };
  };

  const { borderColor, dotColor } = getCardColor();

  const attentionStatus = needsAttention ? String(needsAttention).toLowerCase() : null;
  const health = dealHealth || dealHealthName;
  const healthLower = health ? String(health).toLowerCase() : '';

  const dateType: CardDealType =
    attentionStatus === 'overdue' || healthLower === 'overdue'
      ? 'overdue'
      : attentionStatus === 'warning' || healthLower === 'warning'
        ? 'warning'
        : undefined;

  const [imageError, setImageError] = useState(false);

  return (
    <>
      <Link to={`${RoutePaths.Deals}/${data.dealId}`} style={{ textDecoration: 'none' }}>
        <Box
          sx={{
            textDecoration: 'none',
            color: zeplinColor.BasicBlack,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${borderColor}`,
            borderRadius: '16px',
            padding: '16px',
            gap: '24px',
            flexShrink: 0,
            backgroundColor: zeplinColor.Neutral1,
            ':hover': {
              padding: '15px',
              border: `2px solid ${borderColor}`,
            },
          }}
          draggable='true'
          onDragStart={(e) => onDrag(e, data.name)}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <Typography variant='heading4' sx={{ color: zeplinColor.BasicBlack }}>
                {name}
              </Typography>

              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '100%',
                    backgroundColor: dotColor,
                  }}
                />
                <Box
                  sx={{
                    padding: '3px',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    ':hover': {
                      backgroundColor: zeplinColor.Neutral5,
                      cursor: 'pointer',
                    },
                    gap: '4px',
                  }}
                >
                  <VerticalDotsIcon
                    className={styles['vertical-icon']}
                    onClick={(e: MouseEvent<SVGSVGElement>) => {
                      e.preventDefault();
                      onSettingsClick(e, data);
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {variant !== 'no-pipeline' ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginBottom: '4px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '22px',
                      overflow: 'hidden',
                      width: '24px',
                      height: '24px',
                      backgroundColor: zeplinColor.Brand1,
                      position: 'relative',
                    }}
                  >
                    {profilePicture && !imageError ? (
                      <img
                        src={profilePicture}
                        alt='clientName that the deal is attached too'
                        width={'100%'}
                        height={'100%'}
                        style={{ objectFit: 'cover' }}
                        onError={() => setImageError(true)}
                      />
                    ) : clientName ? (
                      <Typography
                        variant='body3'
                        sx={{
                          color: zeplinColor.Neutral1,
                          fontSize: '12px',
                          fontWeight: 600,
                          lineHeight: '24px',
                        }}
                      >
                        {clientName.charAt(0).toUpperCase()}
                      </Typography>
                    ) : null}
                  </Box>
                  <Typography variant='body3'>{clientName}</Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}
                >
                  <Typography variant='body3'>${formatDealAmount(dealValue || amount)}</Typography>
                  {[formattedPipelineType, formattedStageName].map((item, index) => (
                    <Chip
                      key={item}
                      label={
                        <Typography
                          variant='label1'
                          color={!index ? zeplinColor.Neutral1 : zeplinColor.BasicBlack}
                        >
                          {item}
                        </Typography>
                      }
                      sx={{
                        backgroundColor: !index ? zeplinColor.BasicBlack : zeplinColor.Neutral4,
                        borderRadius: '50px',
                        height: '22px',
                      }}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '22px',
                        overflow: 'hidden',
                        width: '24px',
                        height: '24px',
                        backgroundColor: zeplinColor.Brand1,
                        position: 'relative',
                        flexShrink: 0,
                      }}
                    >
                      {profilePicture && !imageError ? (
                        <img
                          src={profilePicture}
                          alt='clientName that the deal is attached too'
                          width={'100%'}
                          height={'100%'}
                          style={{ objectFit: 'cover' }}
                          onError={() => setImageError(true)}
                        />
                      ) : clientName ? (
                        <Typography
                          variant='body3'
                          sx={{
                            color: zeplinColor.Neutral1,
                            fontSize: '12px',
                            fontWeight: 600,
                            lineHeight: '24px',
                          }}
                        >
                          {clientName.charAt(0).toUpperCase()}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography variant='body3'>{clientName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant='responsiveHeading4'>
                      {formatDealAmount(dealValue || amount)}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <Box sx={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
              <UserIcon />
              <Typography variant='label1'>{ownerName}</Typography>
            </Box>

            <Date
              type={dateType}
              date={closeDate}
              daysDiff={daysDiff}
              velocityTargetDate={velocityTargetDate}
            />
          </Box>
        </Box>
      </Link>
    </>
  );
};

export default DealCard;
